import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ElementRef, OnInit, DestroyRef } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';

import type { AuthView } from '../../shared/auth/auth-dialog/auth-dialog.component';
import { AuthDialogComponent } from '../../shared/auth/auth-dialog/auth-dialog.component';
import { AuthService } from '../../services/auth.service';
import { UserProfileService } from '../../services/user-profile.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss',
  imports: [RouterLink, RouterLinkActive, AuthDialogComponent],
})
export class BannerComponent implements OnInit {
  protected readonly auth = inject(AuthService);
  protected readonly userProfile = inject(UserProfileService);
  private readonly router = inject(Router);

  protected authDialogVisible = signal(false);
  protected authView = signal<AuthView>('login');
  protected userMenuOpen = signal(false);
  private userMenuRef = viewChild('userMenu', { read: ElementRef });
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.auth.showLogin$.subscribe(() => {
      this.openAuthDialog();
    });
    
    // We can just rely on the framework to clean up subscription if we use takeUntilDestroyed
    // But since we just added a manual sub without it, let's manually clean it up:
    const sub = this.auth.showLogin$.subscribe(() => this.openAuthDialog());
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  protected isAdmin = computed(() => {
    const email = this.auth.userEmail()?.trim().toLowerCase();
    const admins = environment.adminEmails?.map((e: string) => e.trim().toLowerCase()) ?? [];
    return !!email && admins.length > 0 && admins.includes(email);
  });

  /** Show display name when set, otherwise email, then 'Profile' as fallback. */
  protected userDisplayLabel = computed(() => {
    const displayName = this.userProfile.profile()?.displayName?.trim();
    if (displayName) return displayName;
    return this.auth.userEmail() ?? 'Profile';
  });

  /** Exam countdown: days left and state flags; null when no valid exam date. */
  protected examCountdown = computed<{
    days: number;
    isPast: boolean;
    isToday: boolean;
    examDateFormatted: string;
  } | null>(() => {
    const examDateStr = this.userProfile.profile()?.examDate?.trim();
    if (!examDateStr) return null;
    const examDate = new Date(examDateStr + 'T00:00:00');
    if (Number.isNaN(examDate.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const examStart = new Date(examDate);
    examStart.setHours(0, 0, 0, 0);
    const diffMs = examStart.getTime() - today.getTime();
    const days = Math.round(diffMs / (24 * 60 * 60 * 1000));
    const isPast = days < 0;
    const isToday = days === 0;
    const examDateFormatted = examStart.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    return { days, isPast, isToday, examDateFormatted };
  });

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const el = this.userMenuRef()?.nativeElement;
    if (el && !el.contains(event.target)) {
      this.userMenuOpen.set(false);
    }
  }

  protected openAuthDialog(): void {
    this.authView.set('login');
    this.authDialogVisible.set(true);
  }

  protected onAuthDialogVisibleChange(visible: boolean): void {
    this.authDialogVisible.set(visible);
  }

  protected onAuthViewChange(view: AuthView): void {
    this.authView.set(view);
  }

  protected onAuthSuccess(): void {
    this.authDialogVisible.set(false);
    this.userProfile.fetchProfile();
    const returnUrl = this.auth.getAndClearReturnUrl();
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    }
  }

  protected async signOut(): Promise<void> {
    await this.auth.signOut();
    this.userProfile.clearProfile();
    this.userMenuOpen.set(false);
    await this.router.navigateByUrl('/');
  }

  protected toggleUserMenu(): void {
    this.userMenuOpen.update((v) => !v);
    if (this.auth.isAuthenticated() && !this.userProfile.profile()) {
      this.userProfile.fetchProfile();
    }
  }
}
